import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';

const TONES = {
  emerald: { ring: '#5FBF86', bg: '#9BE38C', text: '#1B1310' },
  lime: { ring: '#5FBF86', bg: '#9BE38C', text: '#1B1310' },
  amber: { ring: '#E8B531', bg: '#F7C948', text: '#1B1310' },
  orange: { ring: '#F0592B', bg: '#FDE3D8', text: '#1B1310' },
  red: { ring: '#9C7FD6', bg: '#D6B6F2', text: '#1B1310' },
  slate: { ring: '#C9C4BA', bg: '#F6F4EF', text: '#6B665E' },
};

export const FiabiliteBloc = ({ userId, employeeId }) => {
  const [data, setData] = useState(undefined);
  const [hist, setHist] = useState(null);
  useEffect(() => {
    axios.get(`${getApiUrl()}/team/reliability`, { withCredentials: true })
      .then(r => setData((r.data?.members || []).find(m => m.user_id === userId || m.user_id === employeeId) || null))
      .catch(() => setData(null));
    axios.get(`${getApiUrl()}/team/reliability/${userId || employeeId}/history?weeks=8`, { withCredentials: true }).then(r => setHist(r.data)).catch(() => setHist(null));
  }, [userId, employeeId]);
  if (data === undefined) return <p className="text-xs text-[var(--texte-2)]" data-testid="employee-fiche-fiabilite-loading">Calcul de la fiabilité…</p>;
  const score = data?.assigned ? data.score : null;
  // L'absence de données n'est jamais une alerte : gris neutre, jamais rouge.
  const t = score == null ? TONES.slate : TONES[data?.color] || TONES.red;
  const radius = 26; const circ = 2 * Math.PI * radius;
  return (
    <section className="rounded-2xl border p-4 space-y-3 no-poste-print" data-testid="employee-fiche-fiabilite">
    <div className="flex items-center gap-4">
      <div className="relative shrink-0">
        <svg width={68} height={68} viewBox="0 0 68 68" className="-rotate-90">
          <circle cx="34" cy="34" r={radius} stroke={t.bg} strokeWidth="7" fill="none" />
          <circle cx="34" cy="34" r={radius} stroke={t.ring} strokeWidth="7" fill="none" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ - ((score ?? 0) / 100) * circ} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[15px] font-extrabold" style={{ color: t.text, fontFamily: 'var(--font-title)' }}>{score == null ? '—' : `${score}%`}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="hp-kicker">Fiabilité · 30 jours</p>
        <h4 className="leading-tight" data-testid="employee-fiche-fiabilite-label">{score == null ? 'Pas encore de données' : data.label}</h4>
        {score == null ? (
          <p className="text-xs text-[var(--texte-2)] mt-0.5">Assignez-lui des tâches récurrentes dans Suivi des tâches : le score se calcule sur les 30 derniers jours.</p>
        ) : (
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--texte)] mt-1">
            <span><strong className="text-[var(--texte)]">{data.completed}/{data.assigned}</strong> tâches validées</span>
            <span><strong className="text-[var(--texte)]">{data.on_time_rate}%</strong> à l’heure</span>
            {data.streak > 0 && <span className="font-bold" style={{ color: TONES.orange.ring }}>{data.streak} j d’affilée à 100 %</span>}
            {data.color === 'red' && <span className="font-semibold" style={{ color: t.text }}>À reprendre en brief : tâches non cochées dans l’app.</span>}
          </div>
        )}
      </div>
    </div>
    {hist?.weeks?.length > 0 && <HistoriqueSemaines hist={hist} />}
    </section>
  );
};

const toneFor = (score) => score == null ? '#D4D4D8' : score >= 90 ? TONES.emerald.ring : score >= 75 ? TONES.lime.ring : score >= 60 ? TONES.amber.ring : score >= 40 ? TONES.orange.ring : TONES.red.ring;

const HistoriqueSemaines = ({ hist }) => {
  const weeks = hist.weeks;
  const W = 320, H = 96, pad = 6, gap = 6;
  const bw = (W - pad * 2 - gap * (weeks.length - 1)) / weeks.length;
  const pts = weeks.map((w, i) => ({ x: pad + i * (bw + gap) + bw / 2, y: w.score == null ? null : H - 22 - (w.score / 100) * (H - 34) }));
  const path = pts.filter(p => p.y != null).map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const t = hist.tendance;
  return (
    <div className="bg-[var(--remplissage)] rounded-xl p-3" data-testid="employee-fiche-fiabilite-historique">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="hp-kicker">Progression · 8 dernières semaines</p>
        <span className="kit-chip fh-chip" data-testid="employee-fiche-fiabilite-tendance">{t == null ? 'Pas assez de données' : t === 0 ? 'Stable' : `${t > 0 ? '+' : ''}${t} pts vs les 4 semaines d’avant`}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-1 block" role="img" aria-label="Score de fiabilité par semaine">
        {[25, 50, 75].map(v => <line key={v} x1={pad} x2={W - pad} y1={H - 22 - (v / 100) * (H - 34)} y2={H - 22 - (v / 100) * (H - 34)} stroke="#E5E0D6" strokeDasharray="2 3" />)}
        {weeks.map((w, i) => { const x = pad + i * (bw + gap); const h = w.score == null ? 3 : Math.max(3, (w.score / 100) * (H - 34)); return (
          <g key={w.semaine} data-testid={`fiabilite-semaine-${w.semaine}`}>
            <rect x={x} y={H - 22 - h} width={bw} height={h} rx={3} fill={toneFor(w.score)} opacity={w.en_cours ? 0.55 : 0.9} />
            <text x={x + bw / 2} y={H - 9} textAnchor="middle" fontSize="12" fontWeight={w.en_cours ? 800 : 600} fill="#6B7280">{w.label}</text>
            {w.score != null && <text x={x + bw / 2} y={H - 26 - h} textAnchor="middle" fontSize="12" fontWeight="700" fill="#374151">{w.score}</text>}
          </g>); })}
        {path && <path d={path} fill="none" stroke="#1B1310" strokeWidth="1.5" strokeLinejoin="round" opacity="0.6" />}
      </svg>
      <p className="text-xs text-[var(--texte-2)]">Score = 70 % tâches validées + 30 % ponctualité · barre claire = semaine en cours · {weeks.filter(w => w.score == null).length ? 'gris = aucune tâche prévue' : `${weeks.reduce((a, w) => a + w.completed, 0)}/${weeks.reduce((a, w) => a + w.assigned, 0)} tâches validées sur la période`}</p>
    </div>
  );
};
